import { createParser } from "nuqs";
import {
  FieldTypeOptions,
  StringConditions,
  NumberConditions,
  BooleanConditions,
  DateConditions,
  DropdownConditions,
} from "../../schemas/DemoTypes";
import { DataFilterType } from "../../queries/getCrmRequests";
import { isUndefined, omitBy } from "lodash";
import { parseEnum, validateNumber, parseObjectInQueryString } from "./nuqsHelperFunctions";

// Parse data looking like this: filter=id::value|fieldId::value|order::value|
export const nuqsDataFilterParser = createParser<DataFilterType>({
  parse(value) {
    const parsedObject = parseObjectInQueryString({
      queryString: value,
      keyValueSeparator: "|",
      pairSeparator: "::", // double colons ensure that date strings are not split accidentally
    });
    const preValidatedFilterObject = {
      fieldId: validateNumber(parsedObject.fieldId),
      field: parsedObject.field,
      fieldTable: parsedObject.fieldTable,
      order: parsedObject.order,
      type: parsedObject.type,
      condition: parsedObject.condition,
      value: parsedObject.value,
    };
    console.log(parsedObject.fieldTable);
    if (
      !preValidatedFilterObject.field ||
      !preValidatedFilterObject.order ||
      !preValidatedFilterObject.type ||
      !preValidatedFilterObject.value
    ) {
      return null;
    }
    const validatedFieldType = parseEnum(preValidatedFilterObject.type, FieldTypeOptions);
    if (!validatedFieldType) {
      return null;
    }
    //undefined values are still added to the object, and get sent as null to the back end, causing validation errors
    const baseFilter = omitBy(
      {
        fieldId: preValidatedFilterObject.fieldId,
        fieldTable: preValidatedFilterObject.fieldTable,
        field: preValidatedFilterObject.field,
        order: parseInt(preValidatedFilterObject.order),
      },
      (value) => isUndefined(value) || value === "undefined" || value === "null",
    ) as {
      fieldId: number | undefined;
      field: string;
      order: number;
      fieldTable: string | undefined;
    };
    switch (validatedFieldType) {
      case "Text":
      case "TextArea":
      case "Email":
      case "URL":
      case "PhoneNumber":
      case "Address":
        const stringCondition = parseEnum(preValidatedFilterObject.condition, StringConditions);
        if (!stringCondition) {
          return null;
        }
        if (stringCondition === "Empty" || stringCondition === "NotEmpty") {
          return {
            ...baseFilter,
            type: validatedFieldType,
            stringCondition,
          };
        }
        //check if value is an array or just a string
        const splitValue = preValidatedFilterObject.value.split(",");
        if (splitValue.length > 1) {
          return {
            ...baseFilter,
            type: validatedFieldType,
            stringCondition,
            jsonValue: splitValue,
          };
        }
        return {
          ...baseFilter,
          type: validatedFieldType,
          stringCondition,
          stringValue: preValidatedFilterObject.value,
        };
      case "Number":
      case "Currency":
      case "Percent":
        const numberCondition = parseEnum(preValidatedFilterObject.condition, NumberConditions);
        if (!numberCondition) {
          return null;
        }
        if (numberCondition === "Empty" || numberCondition === "NotEmpty") {
          return {
            ...baseFilter,
            type: validatedFieldType,
            numberCondition,
          };
        }
        if (numberCondition === "Between") {
          const [firstValue, secondValue] = preValidatedFilterObject.value.split(",");
          const firstNumberValue = parseFloat(firstValue);
          if (isNaN(firstNumberValue)) {
            return null;
          }
          const secondNumberValue = secondValue ? parseFloat(secondValue) : null;
          if (!secondNumberValue || isNaN(secondNumberValue)) {
            return null;
          }
          if (validatedFieldType === "Number") {
            return {
              ...baseFilter,
              type: validatedFieldType,
              numberCondition,
              integerValue: firstNumberValue,
              secondIntegerValue: secondNumberValue,
            };
          }
          return {
            ...baseFilter,
            type: validatedFieldType,
            numberCondition,
            floatValue: firstNumberValue,
            secondFloatValue: secondNumberValue,
          };
        }
        const numberValue = parseFloat(preValidatedFilterObject.value);
        if (isNaN(numberValue)) {
          return null;
        }
        if (validatedFieldType === "Number") {
          return {
            ...baseFilter,
            type: validatedFieldType,
            numberCondition,
            integerValue: numberValue,
          };
        }
        return {
          ...baseFilter,
          type: validatedFieldType,
          numberCondition,
          floatValue: numberValue,
        };
      case "Checkbox":
        const booleanCondition = parseEnum(preValidatedFilterObject.condition, BooleanConditions);
        if (!booleanCondition) {
          return null;
        }
        const booleanValue = preValidatedFilterObject.value === "true";
        return {
          ...baseFilter,
          type: validatedFieldType,
          booleanCondition,
          booleanValue,
        };
      case "Date":
      case "DateTime":
      case "Time":
        const dateCondition = parseEnum(preValidatedFilterObject.condition, DateConditions);
        if (!dateCondition) {
          return null;
        }
        if (dateCondition === "Empty" || dateCondition === "NotEmpty") {
          return {
            ...baseFilter,
            type: validatedFieldType,
            dateCondition,
          };
        }
        const [firstValue, secondValue] = preValidatedFilterObject.value.split(",");
        const startDateValue = new Date(firstValue);
        if (isNaN(startDateValue.getTime())) {
          //this is a date filter string
          return {
            ...baseFilter,
            type: validatedFieldType,
            dateCondition,
            stringValue: firstValue,
          };
        }
        const endDateValue = secondValue ? new Date(secondValue) : null;
        const endDateIsValidDate = endDateValue ? !isNaN(endDateValue.getTime()) : true;
        if (endDateValue && endDateIsValidDate) {
          return {
            ...baseFilter,
            type: validatedFieldType,
            dateCondition,
            startDateValue: startDateValue?.toISOString(),
            endDateValue: endDateValue.toISOString(),
          };
        }
        if (!startDateValue) {
          return null;
        }
        return {
          ...baseFilter,
          type: validatedFieldType,
          dateCondition,
          startDateValue: startDateValue.toISOString(),
        };
      case "Dropdown":
      case "DropdownMultiSelect":
      case "LookUp":
        const dropdownCondition = parseEnum(preValidatedFilterObject.condition, DropdownConditions);
        if (!dropdownCondition) {
          return null;
        }
        const dropdownValue = preValidatedFilterObject.value.split(",");
        return {
          ...baseFilter,
          type: validatedFieldType,
          dropdownCondition,
          jsonValue: dropdownValue,
        };
      default:
        return null;
    }
  },
  serialize(value) {
    switch (value.type) {
      case "Text":
      case "TextArea":
      case "Email":
      case "URL":
      case "PhoneNumber":
      case "Address":
        if (value.stringCondition === "Empty" || value.stringCondition === "NotEmpty") {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.stringCondition}`;
        }
        if ("stringValue" in value) {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.stringCondition}|value::${value.stringValue}`;
        }
        if ("jsonValue" in value) {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${
            value.order
          }|type::${value.type}|condition::${value.stringCondition}|value::${value.jsonValue.join(",")}`;
        }
        break;
      case "Number":
        if (value.numberCondition === "Empty" || value.numberCondition === "NotEmpty") {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.numberCondition}`;
        }
        if (value.numberCondition === "Between") {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.numberCondition}|value::${value.integerValue},${value.secondIntegerValue}`;
        }
        if ("integerValue" in value) {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.numberCondition}|value::${value.integerValue}`;
        }
        break;
      case "Currency":
      case "Percent":
        if (value.numberCondition === "Empty" || value.numberCondition === "NotEmpty") {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.numberCondition}`;
        }
        if (value.numberCondition === "Between") {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.numberCondition}|value::${value.floatValue},${value.secondFloatValue}`;
        }
        if ("floatValue" in value) {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.numberCondition}|value::${value.floatValue}`;
        }
        break;
      case "Checkbox":
        return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.booleanCondition}|value::${value.booleanValue}`;
      case "Date":
      case "DateTime":
      case "Time":
        if (value.dateCondition === "Empty" || value.dateCondition === "NotEmpty") {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.dateCondition}`;
        }
        if ("stringValue" in value) {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.dateCondition}|value::${value.stringValue}`;
        }
        if ("startDateValue" in value && "endDateValue" in value) {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.dateCondition}|value::${value.startDateValue},${value.endDateValue}`;
        }
        if ("startDateValue" in value) {
          //this is a date filter string
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.dateCondition}|value::${value.startDateValue}`;
        }
        if ("endDateValue" in value) {
          //this is a date filter string
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.dateCondition}|value::,${value.endDateValue}`;
        }
        break;
      case "Dropdown":
      case "DropdownMultiSelect":
      case "LookUp":
        if (value.dropdownCondition === "Empty" || value.dropdownCondition === "NotEmpty") {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${value.order}|type::${value.type}|condition::${value.dropdownCondition}`;
        }
        if ("jsonValue" in value) {
          return `fieldId::${value.fieldId}|field::${value.field}|fieldTable::${value.fieldTable}|order::${
            value.order
          }|type::${value.type}|condition::${value.dropdownCondition}|value::${value.jsonValue.join(",")}`;
        }
        break;
      default:
        return "";
    }
    return "";
  },
});
